import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
  Dialog,
  Collapse,
  Fade,
  makeStyles,
  IconButton,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import React, { FC, useCallback, useEffect, useState } from 'react';
import tinymce from 'tinymce';
import SearchIcon from '@material-ui/icons/Search';
import useNonSFDCTodayPlusNMergeField from '@groove/ui/hooks/useNonSFDCTodayPlusNMergeField';
import useNonSFDCSchedulerLinkMergeField from '@groove/ui/hooks/useNonSFDCSchedulerLinkMergeField';
import { NonSFDCTodayPlusN } from '@groove/ui/Components/NonSFDCTodayPlusN';
import { NonSFDCSchedulerCustomLink } from '@groove/ui/Components/NonSFDCSchedulerCustomLink';
import { isAddDaysValid } from '@groove/ui/helpers/nonSFDCMergeFields';
import { MergeField } from '@groove/ui/type';

import MergeFieldSearchResults from './MergeFieldSearchResults';
import {
  isCustomMergeField,
  isCustomMergeFieldWithDays,
  isCustomSchedulerLink,
  removeNonSFDCMergeFields,
} from './helpers';

const useStyles = makeStyles({
  dialogSize: {
    width: 520,
    maxHeight: 450,
  },
  inputRoot: {
    display: 'block',
    marginBottom: 8,
    height: 32,
    '& > [class*=MuiInputBase-root]': {
      height: 32,
    },
  },
  searchInput: {
    paddingRight: 4,
    height: 32,
    fontSize: 14,
  },
  clearIconButton: {
    height: 24,
    width: 24,
  },
  clearIcon: {
    height: 14,
  },
  label: {
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
  },
  topDivider: {
    marginBottom: 16,
  },
  mergeFieldContainer: {
    flexGrow: 1,
    overflowY: 'scroll',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '20px',
  },
  fadeOut: {
    left: 0,
    width: '100%',
    bottom: 0,
    position: 'sticky',
    height: '25px',
    background:
      'linear-gradient(to bottom, rgba(255,255,255,0.25) 0%,rgba(255,255,255,1) 100%)',
  },
  infoIcon: {
    fontSize: 12,
    paddingLeft: 5,
  },
});

export type MergeFieldGroup = {
  label: string;
  options: MergeField[];
};

interface MergeFieldDialogProps {
  onClose: () => void;
  isOpen: boolean;
  onSelect?: (mergeValue: string) => void;
  excludeNonSFDCMergeFields?: boolean;
  mergeFieldProps: {
    mergeFields: MergeFieldGroup[];
    fallbackRequired?: boolean;
    addFallback?: boolean;
  };
}

const DEFAULT_LINK_TEXT = 'Book a meeting with me';

export const MergeFieldDialog: FC<MergeFieldDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  excludeNonSFDCMergeFields = false,
  mergeFieldProps,
}) => {
  const { mergeFields, fallbackRequired, addFallback = true } = mergeFieldProps;
  const filteredMergeFields = excludeNonSFDCMergeFields
    ? removeNonSFDCMergeFields(mergeFields)
    : mergeFields;
  const [value, setValue] = useState('');
  const [fallbackValue, setFallbackValue] = useState('');

  const [selectedMergeField, setSelectedMergeField] =
    useState<MergeField | null>(null);

  const classes = useStyles({});

  const {
    daysToAdd,
    handleAddDays,
    handleResetDaysToAdd,
    setNonSFDCMergeField,
  } = useNonSFDCTodayPlusNMergeField();

  const {
    isLoading: isMeetingTypesLoading,
    selectedMeetingType,
    meetingTypes,
    customLinkLabel,
    handleMeetingTypeChange,
    handleCustomLinkLabelChange,
  } = useNonSFDCSchedulerLinkMergeField();

  const resetState = (): void => {
    setValue('');
    setFallbackValue('');
    handleResetDaysToAdd();
    setSelectedMergeField(null);
  };

  const isFallbackValueValid = (): boolean => {
    let isValid = true;

    if (fallbackRequired) {
      isValid = isValid && !!fallbackValue;
    }

    return isValid && !fallbackValue.includes('"');
  };

  const showFallback =
    addFallback &&
    selectedMergeField &&
    !isCustomMergeField(selectedMergeField);

  const showNumberOfDays =
    selectedMergeField && isCustomMergeFieldWithDays(selectedMergeField);

  const customSchedulerLink =
    selectedMergeField && isCustomSchedulerLink(selectedMergeField);

  const isInsertDisabled =
    !selectedMergeField ||
    !isFallbackValueValid() ||
    !isAddDaysValid(selectedMergeField, daysToAdd);

  const handleOnChange = useCallback(e => {
    setValue(e.target.value);
    setSelectedMergeField(null);
    handleResetDaysToAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChangeFallbackValue = useCallback(e => {
    setFallbackValue(e.target.value);
  }, []);

  const handleSelectMergeField = useCallback(selectedMergeField => {
    setValue(selectedMergeField.label);
    setSelectedMergeField(selectedMergeField);
  }, []);

  const getFallbackValueErrorText = (): string => {
    if (isFallbackValueValid()) {
      return '';
    }

    if (fallbackRequired) {
      return 'The fallback value is required and cannot contain a "';
    }

    return 'The fallback value cannot contain a "';
  };

  const handleInsert = useCallback(() => {
    if (selectedMergeField) {
      let mergeValue = selectedMergeField.name;
      if (fallbackValue)
        mergeValue = `${selectedMergeField.name} || "${fallbackValue}"`;
      if (isCustomMergeFieldWithDays(selectedMergeField) && daysToAdd) {
        mergeValue = selectedMergeField.name.replace('n', `${daysToAdd}`);
      }
      if (customSchedulerLink) {
        const meetingType = meetingTypes?.find(
          type => type.id === Number(selectedMeetingType),
        );
        if (!meetingType) return;
        const href =
          meetingType.id === -1 // This means default option with default meeting link
            ? selectedMergeField.name
            : `${selectedMergeField.name}_${meetingType.custom_link}`;
        mergeValue = `
        <a href="{!${href}}" rel="noopener">${
          customLinkLabel || DEFAULT_LINK_TEXT
        }</a>
        `.trim();
      }

      const formattedMergeValue = customSchedulerLink
        ? mergeValue
        : `{!${mergeValue}}`;

      if (onSelect) {
        onSelect(formattedMergeValue);
        onClose();
        return;
      }

      tinymce.activeEditor.insertContent(formattedMergeValue);
      onClose();
      window.dispatchEvent(
        new CustomEvent('merge-field-inserted', {
          detail: {
            fallbackAdded: !!fallbackValue,
            mergeField: selectedMergeField.name,
          },
        }),
      );
    }
  }, [
    fallbackValue,
    selectedMergeField,
    onClose,
    onSelect,
    daysToAdd,
    meetingTypes,
    customLinkLabel,
    selectedMeetingType,
    customSchedulerLink,
  ]);

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!showNumberOfDays && !customSchedulerLink) return;
    setNonSFDCMergeField(selectedMergeField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMergeField, showNumberOfDays, customSchedulerLink]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      classes={{ paper: classes.dialogSize }}
    >
      <DialogContent className={classes.dialogContent}>
        {selectedMergeField && (
          <Typography variant="caption" className={classes.label}>
            Selected field
          </Typography>
        )}
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          classes={{
            root: classes.inputRoot,
          }}
          size="small"
          InputProps={{
            classes: { root: classes.searchInput },
            startAdornment: !value ? <SearchIcon color="primary" /> : undefined,
            endAdornment: value ? (
              <IconButton
                className={classes.clearIconButton}
                color="primary"
                size="small"
                onClick={resetState}
              >
                <ClearIcon className={classes.clearIcon} />
              </IconButton>
            ) : undefined,
          }}
          placeholder="Search for a merge field"
          value={value}
          onChange={handleOnChange}
        />
        <Collapse
          in={!selectedMergeField}
          className={classes.mergeFieldContainer}
        >
          <MergeFieldSearchResults
            onSelectMergeField={handleSelectMergeField}
            mergeFields={filteredMergeFields}
            value={value}
          />
          <div className={classes.fadeOut} />
        </Collapse>
        {showFallback && (
          <Fade in={!!selectedMergeField}>
            <>
              <Typography variant="caption" className={classes.label}>
                {fallbackRequired ? 'Fallback *' : 'Fallback'}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                classes={{
                  root: classes.inputRoot,
                }}
                inputProps={{ 'data-testid': 'fallback-input' }}
                placeholder="Fallback to this value when the field is not set"
                onChange={handleOnChangeFallbackValue}
                error={!isFallbackValueValid()}
                helperText={getFallbackValueErrorText()}
              />
            </>
          </Fade>
        )}
        {showNumberOfDays && (
          <Fade in={!!selectedMergeField}>
            <NonSFDCTodayPlusN
              mergeField={selectedMergeField}
              daysToAdd={daysToAdd}
              onChange={handleAddDays}
            />
          </Fade>
        )}
        {customSchedulerLink && (
          <Fade in={!!selectedMergeField}>
            <NonSFDCSchedulerCustomLink
              isLoading={isMeetingTypesLoading}
              meetingTypes={meetingTypes}
              selectedMeetingType={selectedMeetingType}
              onMeetingTypeChange={handleMeetingTypeChange}
              customLinkLabel={customLinkLabel}
              onCustomLinkLabelChange={handleCustomLinkLabelChange}
            />
          </Fade>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          aria-label="Insert"
          disabled={isInsertDisabled || isMeetingTypesLoading}
          onClick={handleInsert}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeFieldDialog;
